import { Sparkles } from "lucide-react";
import { useSite } from "@/contexts/SiteContext";
import { BrandIcon, brandColorFor } from "@/components/icons/BrandIcons";
import SectionCta from "@/components/SectionCta";

// Social icons rendered in colored circles like the mockup
const SOCIAL_ORDER = [
  { key: "whatsapp", brand: "whatsapp", label: "WhatsApp" },
  { key: "whatsapp_community", brand: "whatsapp", label: "WhatsApp" },
  { key: "tiktok", brand: "tiktok", label: "TikTok" },
  { key: "youtube", brand: "youtube", label: "YouTube" },
  { key: "instagram", brand: "instagram", label: "Instagram" },
  { key: "facebook", brand: "facebook", label: "Facebook" },
  { key: "telegram", brand: "telegram", label: "Telegram" },
  { key: "spotify", brand: "spotify", label: "Spotify" },
  { key: "x", brand: "x", label: "X" },
  { key: "linkedin", brand: "linkedin", label: "LinkedIn" },
  { key: "threads", brand: "threads", label: "Threads" },
  { key: "pinterest", brand: "pinterest", label: "Pinterest" },
  { key: "discord", brand: "discord", label: "Discord" },
];

// Splits an admin-provided title into 3 visual lines.
// First two segments end with a period and the LAST WORD of segment #2 is gold-italic.
// If the admin doesn't provide a custom title we fall back to the canonical 3-line Georgiana title.
function renderTitle(rawTitle) {
  const fallback = (
    <h1 data-el="title" data-field="title" className="font-serif tracking-[-0.025em] leading-[0.96] text-[clamp(36px,8.2vw,68px)] lg:text-[clamp(56px,4.5vw,72px)]" style={{ color: "var(--ct-hero-text)" }} data-testid="hero-title">
      <span className="block">Eliberează blocajele.</span>
      <span className="block">Clarifică-ți <em className="italic" style={{ color: "var(--ct-gold)" }}>direcția</em>.</span>
      <span className="block">Transformă-ți viața.</span>
    </h1>
  );
  if (!rawTitle || !rawTitle.trim()) return fallback;
  return (
    <h1 data-el="title" data-field="title" className="font-serif tracking-[-0.025em] leading-[0.96] text-[clamp(36px,8.2vw,68px)] lg:text-[clamp(56px,4.5vw,72px)]" style={{ color: "var(--ct-hero-text)", whiteSpace: "pre-line" }} data-testid="hero-title">
      {rawTitle}
    </h1>
  );
}

export default function Hero() {
  const { site, sectionContent } = useSite();
  const c = sectionContent("hero") || {};
  // Hero content comes from sections.hero.content ONLY (single source of truth).
  // The legacy site.hero is deprecated and no longer read at runtime.
  const title = c.title || "";
  const subtitle = c.subtitle || "Cursuri online, ședințe individuale și programe de transformare care te ajută să câștigi claritate, echilibru emoțional și schimbare profundă.";
  const eyebrow = c.eyebrow || "Terapie · Cursuri · Transformare";
  const primary = { text: c.primary_cta_text || "Rezervă o sesiune", link: c.primary_cta_link || "/contact" };
  // Fallback aliniat cu seed_data.py: /cursuri e un modul dezactivat, nemenționat
  // în documentele de brand — implicitul de cod nu trebuie să-l mai promoveze
  // dacă vreodată câmpul din DB e golit.
  const secondary = { text: c.secondary_cta_text || "Vezi ședințele", link: c.secondary_cta_link || "/sedinte" };
  const portrait = c.portrait_url || "";
  // Static inline fallbacks only. The REAL per-viewport positioning/zoom comes from
  // the generated CSS (content.responsive.images.portrait_url via SectionStyles),
  // which overrides these inline values with !important.
  const desktopObjPos = c.desktop_image_position || "center 25%";
  const mobileObjPos = c.mobile_image_position || "85% 30%";
  const social = site?.social || {};
  const seen = new Set();
  const activeSocials = SOCIAL_ORDER.filter((s) => {
    if (!social[s.key]) return false;
    if (seen.has(s.brand)) return false;
    seen.add(s.brand);
    return true;
  });

  return (
    <section
      data-testid="hero-section"
      className="relative overflow-hidden"
      style={{ background: "var(--ct-hero-bg, var(--ct-bg))" }}
    >
      {/* ============== DESKTOP HERO (≥1024px) ============== */}
      <div className="hidden lg:block relative" style={{ minHeight: "560px" }}>
        <div className="max-w-[1440px] mx-auto px-12 grid grid-cols-12 gap-0 items-center" style={{ minHeight: "560px" }}>
          {/* TEXT — ~48% (col-span-6 visible) */}
          <div className="col-span-6 relative z-10 py-16 pr-8">
            <div data-el="eyebrow" data-testid="hero-eyebrow" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] font-medium mb-6" style={{ color: "var(--ct-gold)" }}>
              <Sparkles size={14} />
              <span data-field="eyebrow">{eyebrow}</span>
            </div>
            {renderTitle(title)}
            <p data-el="subtitle" data-field="subtitle" data-testid="hero-subtitle" className="text-base leading-[1.5] mt-5 max-w-[470px]" style={{ color: "var(--ct-hero-text-muted)" }}>
              {subtitle}
            </p>
            <div data-el="cta_group" className="flex flex-wrap gap-4 mt-7">
              <SectionCta
                content={c}
                prefix="cta"
                fallbackText={primary.text}
                fallbackLink={primary.link}
                className="inline-flex items-center justify-center gap-2 h-12 px-[22px] rounded-[3px] font-semibold text-sm transition-all hover:brightness-105 bg-[var(--ct-btn-primary-bg)] text-[var(--ct-btn-primary-text)] border border-[var(--ct-btn-primary-border)] shadow-[var(--ct-shadow-gold)]"
              />
              <SectionCta
                content={c}
                prefix="cta2"
                fallbackText={secondary.text}
                fallbackLink={secondary.link}
                className="inline-flex items-center justify-center gap-2 h-12 px-[22px] rounded-[3px] font-medium text-sm transition-all hover:bg-[rgba(212,175,55,0.08)] bg-transparent text-[var(--ct-hero-text)] border border-[var(--ct-btn-secondary-border)]"
              />
            </div>

            {/* Social bar inline under buttons — pill */}
            {activeSocials.length > 0 && (
              <div
                className="inline-flex items-center gap-[14px] mt-10 px-[18px] py-[10px] rounded-[18px]"
                style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.14)", width: "fit-content" }}
                data-testid="hero-social-bar"
              >
                {activeSocials.map((s) => {
                  const color = brandColorFor(s.brand);
                  return (
                    <a
                      key={s.key}
                      href={social[s.key]}
                      target="_blank"
                      rel="noreferrer"
                      title={s.label}
                      aria-label={s.label}
                      data-testid={`hero-social-${s.key}`}
                      className="inline-flex items-center justify-center rounded-full transition-transform hover:scale-110"
                      style={{ width: "34px", height: "34px", background: color }}
                    >
                      <BrandIcon kind={s.brand} size={16} color="#FFFFFF" />
                    </a>
                  );
                })}
              </div>
            )}
          </div>

          {/* IMAGE — ~52% (col-span-6) */}
          <div className="col-span-6 relative h-full" style={{ minHeight: "560px" }}>
            {portrait && (
              <img
                src={portrait}
                alt="Terapeut Georgiana"
                data-testid="hero-portrait"
                data-img="portrait_url"
                fetchPriority="high"
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover"
                style={{ objectPosition: desktopObjPos }}
              />
            )}
            {/* DISSOLVE GRADIENT — fades into the hero background on the LEFT edge, wide and seamless.
                data-el → repoziționabil din editorul vizual (free-transform). În producție rămâne
                pointer-events:none; devine selectabil doar în modul editor. */}
            <div
              data-el="hero_gradient"
              data-ct-move-bg
              data-testid="hero-gradient"
              className="absolute inset-0 pointer-events-none"
              style={{
                // Cutie FIXĂ full-cover (inset-0) — NU se translatează, deci nu-și expune
                // niciodată marginea pe fotografie. „Mutarea" din editor glisează doar
                // `background-position` (data-ct-move-bg → prop `bgx`). Imaginea gradient e
                // 300% lățime: solid pe stânga, transparent pe dreapta, iar centrul (poziția
                // implicită 50%) arată exact dizolvarea de dinainte. Glisezi stânga/dreapta
                // fără margini/„linie".
                backgroundImage:
                  "linear-gradient(90deg, var(--ct-hero-solid) 0%, var(--ct-hero-solid) 33.33%, rgba(var(--ct-hero-fade-rgb),0.92) 37.3%, rgba(var(--ct-hero-fade-rgb),0.55) 43.3%, rgba(var(--ct-hero-fade-rgb),0.18) 48.3%, rgba(var(--ct-hero-fade-rgb),0) 52.6%, rgba(var(--ct-hero-fade-rgb),0) 100%)",
                backgroundSize: "300% 100%",
                backgroundPosition: "50% 0",
                backgroundRepeat: "no-repeat",
              }}
            />
            {/* Subtle vertical dissolve top & bottom for seamless edges */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "linear-gradient(180deg, var(--ct-hero-solid) 0%, rgba(var(--ct-hero-fade-rgb),0.4) 5%, rgba(var(--ct-hero-fade-rgb),0) 18%, rgba(var(--ct-hero-fade-rgb),0) 82%, rgba(var(--ct-hero-fade-rgb),0.4) 95%, var(--ct-hero-solid) 100%)",
              }}
            />
            {/* Vignetă + grade cald peste portret — topește fotografia în bleumarin, ca în mockup */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "radial-gradient(120% 100% at 62% 40%, rgba(212,175,55,0.05) 0%, rgba(0,0,0,0) 45%, rgba(2,8,16,0.45) 100%)",
              }}
            />
          </div>
        </div>
      </div>

      {/* ============== MOBILE / TABLET HERO (<1024px) ============== */}
      <div className="lg:hidden relative overflow-hidden" style={{ minHeight: "440px" }}>
        {/* Image — full-bleed background covering the entire hero area */}
        {portrait && (
          <img
            src={portrait}
            alt="Terapeut Georgiana"
            data-testid="hero-portrait-mobile"
            data-img="portrait_url"
            fetchPriority="high"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ objectPosition: mobileObjPos, zIndex: 0 }}
          />
        )}
        {/* Strong cream dissolve from left (text area) → fully transparent toward image.
            data-el → repoziționabil din editor (free-transform), pointer-events:none în producție. */}
        <div
          data-el="hero_gradient"
          data-ct-move-bg
          data-testid="hero-gradient-mobile"
          className="absolute inset-0 pointer-events-none"
          style={{
            // Cutie fixă full-cover + glisare pe background-position (vezi desktop) →
            // fără „linie" la mutare. Imagine 300%, centrată implicit.
            backgroundImage:
              "linear-gradient(95deg, var(--ct-hero-solid) 0%, var(--ct-hero-solid) 46%, rgba(var(--ct-hero-fade-rgb),0.96) 50.6%, rgba(var(--ct-hero-fade-rgb),0.55) 56.6%, rgba(var(--ct-hero-fade-rgb),0.15) 62.6%, rgba(var(--ct-hero-fade-rgb),0) 66.6%, rgba(var(--ct-hero-fade-rgb),0) 100%)",
            backgroundSize: "300% 100%",
            backgroundPosition: "50% 0",
            backgroundRepeat: "no-repeat",
            zIndex: 1,
          }}
        />
        {/* Top + bottom subtle fade for seamless integration */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(180deg, var(--ct-hero-solid) 0%, rgba(var(--ct-hero-fade-rgb),0.25) 12%, rgba(var(--ct-hero-fade-rgb),0) 30%, rgba(var(--ct-hero-fade-rgb),0) 70%, rgba(var(--ct-hero-fade-rgb),0.4) 92%, var(--ct-hero-solid) 100%)",
            zIndex: 2,
          }}
        />

        {/* Content layer */}
        <div className="relative z-10 px-4 pt-5 pb-6">
          <div className="max-w-[62%] space-y-3">
            <div data-el="eyebrow" data-testid="hero-eyebrow" className="inline-flex items-center gap-1.5 text-[9px] uppercase tracking-[0.22em] font-medium" style={{ color: "var(--ct-gold)" }}>
              <Sparkles size={11} />
              <span data-field="eyebrow">{eyebrow}</span>
            </div>
            <div data-el="title" data-field="title" data-testid="hero-title" className="text-[34px] leading-[0.98] tracking-[-0.02em] font-serif" style={{ color: "var(--ct-hero-text)", whiteSpace: "pre-line" }}>
              {title || (
                <>
                  <span className="block">Eliberează blocajele.</span>
                  <span className="block">Clarifică-ți <em className="italic" style={{ color: "var(--ct-gold)" }}>direcția</em>.</span>
                  <span className="block">Transformă-ți viața.</span>
                </>
              )}
            </div>
            <p data-el="subtitle" data-field="subtitle" data-testid="hero-subtitle" className="text-[13px] leading-[1.4] max-w-[260px]" style={{ color: "var(--ct-hero-text-muted)" }}>
              {subtitle}
            </p>
            <div data-el="cta_group" className="flex flex-col gap-[10px] max-w-[240px] pt-2">
              <SectionCta
                content={c}
                prefix="cta"
                fallbackText={primary.text}
                fallbackLink={primary.link}
                className="inline-flex items-center justify-center gap-2 h-11 rounded-[3px] text-sm font-semibold bg-[var(--ct-btn-primary-bg)] text-[var(--ct-btn-primary-text)] shadow-[var(--ct-shadow-gold)]"
              />
              <SectionCta
                content={c}
                prefix="cta2"
                fallbackText={secondary.text}
                fallbackLink={secondary.link}
                className="inline-flex items-center justify-center gap-2 h-11 rounded-[3px] text-sm font-medium bg-transparent text-[var(--ct-hero-text)] border border-[var(--ct-btn-secondary-border)]"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Social bar — full-width strip immediately below hero on mobile */}
      <div className="lg:hidden" style={{ background: "var(--ct-bg)" }}>
        {activeSocials.length > 0 && (
          <div className="mx-4 mt-3 mb-1 p-[10px] rounded-[14px] flex justify-center gap-3 flex-wrap" style={{ background: "var(--ct-surface)", border: "1px solid var(--ct-border)" }} data-testid="hero-social-bar-mobile">
            {activeSocials.map((s) => {
              const color = brandColorFor(s.brand);
              return (
                <a
                  key={s.key}
                  href={social[s.key]}
                  target="_blank"
                  rel="noreferrer"
                  title={s.label}
                  aria-label={s.label}
                  className="inline-flex items-center justify-center rounded-full transition-transform hover:scale-110 active:scale-95"
                  style={{ width: "38px", height: "38px", background: color }}
                >
                  <BrandIcon kind={s.brand} size={18} color="#FFFFFF" />
                </a>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
